import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import API_URL from '../config';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { AiFillLike, AiOutlineLike } from "react-icons/ai";
import { MdFavorite, MdFavoriteBorder } from "react-icons/md";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { IoStatsChart } from "react-icons/io5";
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

function ArticleDetail() {
  const [article, setArticle] = useState(null);
  const { id } = useParams();
  const { t,i18n } = useTranslation();
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    async function fetchArticle() {
      try {
        const response = await axios.get(`${API_URL}/api/articles/${id}`);
        const articleData = response.data;
  
        setArticle({
          ...articleData,
          //如果你的頁面可能在未來需要從 API 動態獲取某些 SEO 屬性，保留 enableDynamicFill={true} 作為後備機制
          isFavorited: articleData.isFavorited || false, // 確保有收藏屬性
        });
        
        // 獲取該文章的 SEO 設定
        try {
          const seoResponse = await axios.get(`${API_URL}/api/seo-settings/articles/${id}`);
          if (seoResponse.data.success && seoResponse.data.data) {
            console.log('Fetched SEO settings:', seoResponse.data.data);
          }
        } catch (seoErr) {
          console.error('Error fetching SEO settings:', seoErr);
        }

        // 呼叫增加瀏覽數的 API
        await axios.post(`${API_URL}/api/articles/${id}/view`);
      } catch (err) {
        console.error('Error fetching article:', err);
      }
    }
    fetchArticle();
  }, [id]);
  

  // 處理未登入用戶的彈窗邏輯
  const handleUnauthorizedAction = () => {
    setShowLoginModal(true);
    setTimeout(() => {
      window.location.href = `${API_URL}/api/auth/google`; // 3 秒後自動跳轉
    }, 3000);
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        {t('loading')}
      </div>
    );
  }

    // 根據目前語系決定顯示哪個內容
    const contentToRender =
    article &&
    i18n.language === 'en' &&
    article.translations &&
    article.translations.en &&
    typeof article.translations.en === 'object'
      ? article.translations.en.content || ''
      : article?.content || t("noContentAvailable");

    const titleToRender =
    article &&
    i18n.language === 'en' &&
    article.translations &&
    article.translations.en &&
    typeof article.translations.en === 'object'
      ? article.translations.en.title || article.title
      : article.title;
    
    const tagsToRender =
      article &&
      i18n.language === 'en' &&
      article.translations &&
      article.translations.en &&
      Array.isArray(article.translations.en.tags)
        ? article.translations.en.tags
        : article.tags;
    

  // 點讚邏輯
  const handleLike = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/articles/${id}/like`, {}, { withCredentials: true });
      setArticle((prevArticle) => ({
        ...prevArticle,
        likes: response.data.likeCount,
        isLiked: response.data.liked, // 新增更新按讚狀態
      }));
    } catch (err) {
      if (err.response && err.response.status === 401) {
        handleUnauthorizedAction(); // 觸發未登入的彈窗
      } else {
        console.error("Error liking article:", err);
        toast.error(t("likeError"));
      }
    }
  };
  

  // 收藏邏輯
  const handleFavorite = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/articles/${id}/favorite`, {}, { withCredentials: true });
      setArticle((prevArticle) => ({
        ...prevArticle,
        isFavorited: response.data.favorited,
      }));

      toast.success(response.data.favorited ? t('addedToFavorites') : t('removedFromFavorites'));
    } catch (err) {
      if (err.response && err.response.status === 401) {
        handleUnauthorizedAction(); // 觸發未登入的彈窗
      } else {
        console.error('Error favoriting article:', err);
        toast.error(t('favoriteError'));
      }
    }
  };
  
  

  // 構建完整的文章 URL
  const articleUrl = `${window.location.origin}/articles/${id}`;
  // 構建文章摘要（用於 Open Graph 描述）
  const articleDescription = contentToRender.substring(0, 160).replace(/\n/g, ' ');

  return (
    <div className="container mx-auto px-4 py-8">
      {article && (
        <>
          <SEO
            path={`/articles/${id}`}
            title={titleToRender}
            description={article.summary}
            keywords={article.tags?.join(', ')}
            author={article.author?.name || '區塊鏈學院'}
            publishedTime={article.createdAt}
            modifiedTime={article.updatedAt}
            type="article"
            siteName="區塊鏈學院"
            ogImage={`${article.images?.[0] || `${API_URL}/default-article-image.jpg`}`}
            ogType="article"
            ogUrl={`${API_URL}/articles/${id}`}
            twitterCard="summary_large_image"
            twitterImageAlt={titleToRender}
            canonicalUrl={`${API_URL}/articles/${id}`}
            robots="index, follow"
            imageWidth="1200"
            imageHeight="630"
            imageAlt={titleToRender}
            section="區塊鏈學院文章"
            tags={article.tags}
            enableDynamicFill={false}
          />
          <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-black dark:text-white">
            {/* 彈窗 */}
            {showLoginModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                  <h2 className="text-xl font-bold">{t('notLoggedIn')}</h2>
                  <p className="mt-2">{t('redirectingToGoogleLogin')}</p>
                  <div className="mt-4 flex justify-center">
                    <img src="/loading.gif" alt="Loading..." className="w-16 h-16" />
                  </div>
                </div>
              </div>
            )}

            {/* Hero Banner */}
            <div
              className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white text-3xl font-bold"
              style={{ backgroundImage: `url('/images/myReportsBanner.png')` }}
            >
              {t('blockchainAcademy')}
            </div>
            {/* 面包屑導航：區塊鏈學院 > 文章列表 > 文章內容 */}
            <div className="bg-gray-200 dark:bg-gray-700 py-2">
              <div className="container mx-auto px-4">
                <nav className="text-sm">
                  <Link to="/academy" className="text-blue-500 hover:underline">
                    {t('blockchainAcademy')}
                  </Link>
                  <span className="mx-2"> &gt; </span>
                  <Link to="/academy/articles" className="text-blue-500 hover:underline">
                    {t('articles')}
                  </Link>
                  <span className="mx-2"> &gt; </span>
                  <span>{t('articleDetail')}</span>
                </nav>
              </div>
            </div>

            <div className="container mx-auto px-4 py-6">
              {/* 按鈕功能 */}
              <div className="flex items-center justify-end space-x-4 mb-2">
                <div className="flex items-center space-x-1">
                  <IoStatsChart />
                  <span>{article.views != null ? article.views : 0}</span>
                </div>
                <button
                  onClick={handleLike}
                  className="text-red-500 flex items-center space-x-1"
                >
                  {article.isLiked ? <AiFillLike /> : <AiOutlineLike />}
                  <span>{Array.isArray(article.likes) ? article.likes.length : article.likes || 0}</span>
                </button>
                <button
                  onClick={handleFavorite}
                  className="text-yellow-500 flex items-center space-x-1"
                >
                  {article.isFavorited ? <MdFavorite /> : <MdFavoriteBorder />}
                  <span>{article.isFavorited ? t('favorited') : t('favorite')}</span>
                </button>

                {/* 分享按鈕 */}
                <a
                  href={`https://line.me/R/msg/text/?${article.title} - ${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-400 hover:text-green-600"
                >
                  <i className="fab fa-line"></i>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700"
                >
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${article.title} - ${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700"
                >
                  <i className="fab fa-twitter"></i>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 hover:text-blue-900"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a
                  href={`https://api.whatsapp.com/send?text=${article.title} - ${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:text-green-700"
                >
                  <i className="fab fa-whatsapp"></i>
                </a>
                <a
                  href={`https://telegram.me/share/url?url=${window.location.href}&text=${article.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-600"
                >
                  <i className="fab fa-telegram-plane"></i>
                </a>
                <a
                  href={`https://reddit.com/submit?url=${window.location.href}&title=${article.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-500 hover:text-orange-700"
                >
                  <i className="fab fa-reddit"></i>
                </a>
                <button
                  onClick={() => navigator.clipboard.writeText(window.location.href)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-link"></i>
                </button>
              </div>



              {/* 文章內容部分 */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              {/* 標題 */}
              <h1 className="text-4xl font-bold mb-4">{titleToRender}</h1>
              
              {/* 時間和標籤 */}
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-6">
                {/* 顯示時間 */}
                <p>{new Date(article.createdAt).toLocaleString()}</p>

                {/* 顯示標籤 */}
                <div className="flex flex-wrap space-x-2">
                  {tagsToRender?.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded dark:bg-blue-200 dark:text-blue-800"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* 封面圖片 */}
              {article.images && article.images.length > 0 && (
                <div className="mb-6">
                  <img
                    src={article.images[0]}
                    alt={titleToRender}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              )}

              {/* 文章內容 */}
              <div className="prose dark:prose-invert max-w-none">
                <ReactMarkdown
                  children={contentToRender}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                />
              </div>
            </div>


            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ArticleDetail;